import React from "react";
import {Box, Button, Form, FormGroup, SubmitButton, Text, TextInput} from "@unibuddy/patron";
import * as yup from "yup";
import {FormikHelpers} from "formik";
import styled from "styled-components";
import {useAnalytics} from "@unibuddy/tracking";

import {events} from "../../../../../../analytics/productSpecification";
import {useUniversityContext} from "../../../../../../providers/UniversityProvider";

const FREE_TEXT_CHARACTER_LIMIT = 250;
export const CONVERSATION_FEEDBACK_POPUP = "Conversation Feedback Collection Popup";

type ConversationFeedbackFreeTextProps = {
    handleSubmit: (freeText: string) => Promise<void>;
    handleDismiss: () => void;
};

type FormValues = {
    freeText: string;
};

const StyledFromGroupWrapper = styled(Box)`
    span * {
        font-size: 0.875rem;
    }
`;

export const ConversationFeedbackFreeTextForm = ({
    handleSubmit,
    handleDismiss,
}: ConversationFeedbackFreeTextProps) => {
    const {trackEvent} = useAnalytics();
    const {universitySlug} = useUniversityContext();

    return (
        <Form
            initialValues={{freeText: ""}}
            validationSchema={{
                freeText: yup
                    .string()
                    .max(FREE_TEXT_CHARACTER_LIMIT, "Please provide at most 250 characters")
                    .min(6, "Please provide at least 6 characters")
                    .required("Please provide your feedback or skip"),
            }}
            onSubmit={async (values: FormValues, {setSubmitting}: FormikHelpers<FormValues>) => {
                setSubmitting(true);
                trackEvent(events.conversationFeedbackSubmitButtonClicked.name, {
                    product: "Assistant",
                    cta: "Submit",
                    where: CONVERSATION_FEEDBACK_POPUP,
                    universitySlug,
                });
                try {
                    await handleSubmit(values.freeText);
                } catch (error) {
                    console.error(error);
                    setSubmitting(false);
                }
            }}
        >
            {(props: {values: FormValues; isSubmitting: boolean}) => {
                const {values, isSubmitting} = props;
                return (
                    <Box paddingTop="xsmall" w="100%">
                        <StyledFromGroupWrapper paddingBottom="small">
                            <FormGroup
                                required
                                name="freeText"
                                label="We'd love to know why"
                                helpTop={
                                    <Box paddingY="small">
                                        <Text size="small">
                                            Please explain why you gave your rating below
                                        </Text>
                                    </Box>
                                }
                            >
                                <TextInput
                                    required
                                    value={values.freeText ?? ""}
                                    characterLimit={FREE_TEXT_CHARACTER_LIMIT}
                                    name="freeText"
                                />
                            </FormGroup>
                        </StyledFromGroupWrapper>
                        <Box gap="small" display="flex">
                            <Button
                                color="default"
                                ghost
                                block
                                size="sm"
                                onClick={() => {
                                    trackEvent(events.conversationFeedbackSkipButtonClicked.name, {
                                        product: "Assistant",
                                        cta: "Skip",
                                        where: CONVERSATION_FEEDBACK_POPUP,
                                        universitySlug,
                                    });
                                    handleDismiss();
                                }}
                                disabled={isSubmitting}
                            >
                                Skip
                            </Button>
                            <SubmitButton
                                color="primary"
                                block
                                size="sm"
                                disabled={values.freeText.length === 0}
                            >
                                Submit
                            </SubmitButton>
                        </Box>
                    </Box>
                );
            }}
        </Form>
    );
};
